
if (!customElements.get("nss-tabs")) {
    customElements.define("nss-tabs", class NSSTabs extends HTMLElement {
        constructor() {
            super();

            // Default Selectors
            this.selectors = {
                tablist: ".js-nss-tabs-tablist",
                tablistToggle: ".js-nss-tabs-tablist-toggle",
                tabs: ".js-nss-tabs-tab",
                tabpanels: ".js-nss-tabs-tabpanel",
                groups: ".js-nss-tabs-group",
            };

            // Pre-bind methods to ensure 'this' context and consistent references for event listeners
            this.handleToggleClick = this.handleToggleClick.bind(this);
            this.handleTabClick = this.handleTabClick.bind(this);
            this.handleTabChange = this.handleTabChange.bind(this);
            this.handleTabHover = this.handleTabHover.bind(this);
            this.handleTabClose = this.handleTabClose.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);
            this.handleEditorEvent = this.handleEditorEvent.bind(this);

            this._initialized = false;
        }

        loadConfig() {
            const configScript = this.querySelector(".js-tabs-config");
            if (!configScript) return;

            try {
                const config = JSON.parse(configScript.textContent.trim());
                if (config.selectors) {
                    this.selectors = { ...this.selectors, ...config.selectors };
                }
            } catch (error) {
                console.warn("Invalid tabs JSON config", error);
            }
        }

        connectedCallback() {
            if (this._initialized) return;

            this.loadConfig();

            // Element References
            this.tablist = this.querySelector(this.selectors.tablist);
            this.tablistToggle = this.querySelector(this.selectors.tablistToggle);
            this.groups = this.querySelectorAll(this.selectors.groups);
            this.tabs = Array.from(this.querySelectorAll(this.selectors.tabs));
            this.tabpanels = Array.from(this.querySelectorAll(this.selectors.tabpanels));

            if (!this.tablist) return;

            this.orientation = this.tablist.getAttribute("aria-orientation") || "horizontal";

            // Set initial focus index based on aria-selected
            const initialIndex = this.tabs.findIndex(t => t.getAttribute("aria-selected") === "true");
            this.focusedTabIndex = initialIndex >= 0 ? initialIndex : 0;

            // 1. Toggle Button Listener
            this.tablistToggle?.addEventListener("click", this.handleToggleClick);

            // 2. Tablist Listeners (Select vs Buttons)
            if (this.tablist.tagName === "SELECT") {
                this.tablist.addEventListener("change", this.handleTabChange);
            } else {
                this.tablist.addEventListener("keydown", this.handleKeydown);
                this.tablist.addEventListener("click", this.handleTabClick);
            }

            // 3. Hover Listeners (Groups)
            this.groups.forEach(el => {
                el.addEventListener("mouseenter", this.handleTabHover);
                el.addEventListener("mouseleave", this.handleTabClose);
            });

            // 4. Shopify Editor Listeners
            const editorEvents = [
                `shopify:block:select`, `shopify:block:deselect`,
                `shopify:section:load`, `shopify:section:select`
            ];
            editorEvents.forEach(event => this.addEventListener(event, this.handleEditorEvent));

            this._initialized = true;
        }

        disconnectedCallback() {
            // Remove listeners using the bound references
            this.tablistToggle?.removeEventListener("click", this.handleToggleClick);

            if (this.tablist) {
                this.tablist.removeEventListener("change", this.handleTabChange);
                this.tablist.removeEventListener("click", this.handleTabClick);
                this.tablist.removeEventListener("keydown", this.handleKeydown);
            }

            this.groups.forEach(el => {
                el.removeEventListener("mouseenter", this.handleTabHover);
                el.removeEventListener("mouseleave", this.handleTabClose);
            });

            const editorEvents = [
                `shopify:block:select`, `shopify:block:deselect`,
                `shopify:section:load`, `shopify:section:select`
            ];
            editorEvents.forEach(event => this.removeEventListener(event, this.handleEditorEvent));

            this._initialized = false;
        }

        handleToggleClick() {
            this.tablist.classList.toggle("is-open");
        }

        handleTabClick(e) {
            const clickedTab = e.target.closest(this.selectors.tabs);
            if (clickedTab) {
                const index = this.tabs.indexOf(clickedTab);
                this.selectTab(index);
                this.tablist.classList.remove("is-open");
            }
        }

        handleTabChange(e) {
            this.selectTab(e.target.selectedIndex);
        }

        handleTabHover(e) {
            if (window.matchMedia('(min-width: 990px)').matches) {
                const group = e.target.closest(this.selectors.groups);
                const hoverTab = group?.querySelector(this.selectors.tabs);
                if (hoverTab) {
                    const index = this.tabs.indexOf(hoverTab);
                    this.selectTab(index);
                }
            }
        }

        handleTabClose() {
            // Optional: Logic to hide panels when mouse leaves the group on desktop
            if (window.matchMedia('(min-width: 990px)').matches) {
                // If you want tabs to stay open, leave this empty.
                // To close on mouseout, uncomment below:
                /*
                this.tabs.forEach(tab => {
                    tab.setAttribute("aria-selected", "false");
                    tab.setAttribute("tabindex", "-1");
                });
                this.tabpanels.forEach(p => p.classList.remove("is-visible"));
                */
            }
        }

        selectTab(index) {
            if (index === -1 || !this.tabs[index]) return;

            const selectedTab = this.tabs[index];
            const panelId = selectedTab.getAttribute("aria-controls");
            const targetPanel = this.querySelector(`#${panelId}`);

            // GUARD: Prevent double firing if already selected
            const isAlreadySelected = selectedTab.getAttribute("aria-selected") === "true";
            const isPanelVisible = targetPanel?.classList.contains("is-visible");

            // Toggle logic for mobile (if clicking the same tab, close it)
            if (isAlreadySelected && isPanelVisible && !window.matchMedia('(min-width: 990px)').matches) {
                selectedTab.setAttribute("aria-selected", "false");
                targetPanel.classList.remove("is-visible");
                return;
            }

            // Standard selection logic
            if (isAlreadySelected && isPanelVisible) return;

            this.tabs.forEach((tab, i) => {
                const isCurrent = i === index;
                tab.setAttribute("aria-selected", isCurrent);
                tab.setAttribute("tabindex", isCurrent ? "0" : "-1");
            });

            this.tabpanels.forEach(panel => {
                panel.classList.remove("is-visible");
            });

            if (targetPanel) {
                targetPanel.classList.add("is-visible");
            }

            this.focusedTabIndex = index;

            if (this.tablistToggle) {
                this.tablistToggle.innerText = selectedTab.innerText;
            }
        }

        handleKeydown(e) {
            const horizontalKeys = {
                ArrowLeft: () => this.selectPreviousTab(),
                ArrowRight: () => this.selectNextTab(),
            };
            const verticalKeys = {
                ArrowUp: () => this.selectPreviousTab(),
                ArrowDown: () => this.selectNextTab(),
            };
            const commonKeys = {
                Home: () => this.selectFirstTab(),
                End: () => this.selectLastTab(),
            };

            const keyHandlers = {
                ...commonKeys,
                ...(this.orientation === "vertical" ? verticalKeys : horizontalKeys),
            };

            if (keyHandlers[e.key]) {
                e.preventDefault();
                keyHandlers[e.key]();
            }
        }

        selectPreviousTab() {
            const index = (this.focusedTabIndex - 1 + this.tabs.length) % this.tabs.length;
            this.selectTab(index);
            this.tabs[index].focus();
        }

        selectNextTab() {
            const index = (this.focusedTabIndex + 1) % this.tabs.length;
            this.selectTab(index);
            this.tabs[index].focus();
        }

        selectFirstTab() {
            this.selectTab(0);
            this.tabs[0].focus();
        }

        selectLastTab() {
            const lastIndex = this.tabs.length - 1;
            this.selectTab(lastIndex);
            this.tabs[lastIndex].focus();
        }

        handleEditorEvent(e) {
            switch (e.type) {
                case `shopify:block:select`:
                    const blockId = e.detail.blockId;
                    const blockEl = blockId ? this.querySelector(`[data-block-id="${blockId}"]`) : null;
                    if (blockEl) {
                        const panel = blockEl.closest(this.selectors.tabpanels);
                        if (panel) {
                            const tab = this.querySelector(`[aria-controls='${panel.id}']`);
                            if (tab) {
                                const index = this.tabs.indexOf(tab);
                                this.selectTab(index);
                            }
                        }
                    }
                    break;
            }
        }
    });
}